import { useState } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  LayoutGrid, GraduationCap, Award, FolderOpen, Users, Trophy,
  Calendar, Shield, Sun, Moon, LogOut, Menu, X, Search,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Logo, Avatar, RoleBadge } from "@/components/Brand";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/community", label: "Community", icon: LayoutGrid, testid: "sidebar-nav-community" },
  { to: "/classroom", label: "Classroom", icon: GraduationCap, testid: "sidebar-nav-classroom" },
  { to: "/cpd", label: "CPD & Certificates", icon: Award, testid: "sidebar-nav-cpd" },
  { to: "/resources", label: "Resource library", icon: FolderOpen, testid: "sidebar-nav-resources" },
  { to: "/members", label: "Members", icon: Users, testid: "sidebar-nav-members" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, testid: "sidebar-nav-leaderboard" },
];

function SidebarContent({ isAdmin, onNavigate }) {
  const linkClass = ({ isActive }) =>
    cn(
      "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
      isActive
        ? "bg-[#33B6E4]/15 text-white"
        : "text-slate-400 hover:bg-white/5 hover:text-white"
    );
  return (
    <div className="flex h-full flex-col text-white">
      <div className="px-5 py-5">
        <Logo wm={22} tone="dark" />
      </div>
      <nav className="flex-1 space-y-1 px-3 py-2">
        {NAV.map((item) => (
          <NavLink key={item.to} to={item.to} data-testid={item.testid} onClick={onNavigate} className={linkClass}>
            {({ isActive }) => (
              <>
                <item.icon className={cn("h-[18px] w-[18px]", isActive ? "text-[#33B6E4]" : "")} />
                {item.label}
              </>
            )}
          </NavLink>
        ))}

        {isAdmin && (
          <>
            <div className="px-3 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">
              Administration
            </div>
            <NavLink to="/admin" data-testid="sidebar-nav-admin" onClick={onNavigate} className={linkClass}>
              {({ isActive }) => (
                <>
                  <Shield className={cn("h-[18px] w-[18px]", isActive ? "text-[#33B6E4]" : "")} />
                  Admin dashboard
                </>
              )}
            </NavLink>
          </>
        )}
      </nav>
      <div className="px-4 py-4 text-[11px] text-slate-500">trueacademy.uk</div>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  if (user === null) {
    return <div className="grid min-h-screen place-items-center text-muted-foreground">Loading…</div>;
  }

  const isAdmin = ["super_admin", "trainer"].includes(user.role);
  const seedWarning = user.role === "super_admin" && user.must_change_password;

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[260px] bg-[#0D0F12] lg:block">
        <SidebarContent isAdmin={isAdmin} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[280px] bg-[#0D0F12] shadow-xl">
            <button
              className="absolute right-3 top-4 rounded-md p-1 text-slate-400 hover:text-white"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-close"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent isAdmin={isAdmin} onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className="lg:pl-[260px]">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 bg-[#0D0F12] px-4 text-white sm:px-6">
          <button
            className="rounded-md p-2 text-slate-300 hover:bg-white/10 lg:hidden"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-open"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="hidden flex-1 items-center gap-2 sm:flex">
            <div className="relative w-full max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="global-search-input"
                placeholder="Search the academy…"
                className="h-9 w-full rounded-lg border border-white/10 bg-white/5 pl-9 pr-3 text-sm text-white placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-[#33B6E4]"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.target.value.trim()) {
                    navigate(`/classroom?q=${encodeURIComponent(e.target.value.trim())}`);
                  }
                }}
              />
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={toggle}
              data-testid="theme-toggle"
              className="rounded-lg p-2 text-slate-300 hover:bg-white/10"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-lg p-1 pr-2 hover:bg-white/10" data-testid="user-menu-trigger">
                  <Avatar src={user.photo_url} name={user.name} size={32} />
                  <div className="hidden text-left leading-tight sm:block">
                    <div className="text-[13px] font-semibold text-white">{user.name}</div>
                    <div className="text-[11px] text-slate-400">{user.company}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <div className="text-sm font-semibold">{user.name}</div>
                  <div className="mb-1.5 text-xs text-muted-foreground">{user.email}</div>
                  <RoleBadge role={user.role} />
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/members")} data-testid="menu-profile">
                  <Users className="mr-2 h-4 w-4" /> Members directory
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {seedWarning && (
          <div
            data-testid="seed-warning-banner"
            className="border-b border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200 sm:px-6"
          >
            <strong>Default credentials in use.</strong> Change the Super Admin password and remove seed data before go-live
            (Admin → Settings).
          </div>
        )}

        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
